import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import { formatDateTime } from '../../format';
import useSort, { applySort } from '../../hooks/useSort';

/**
 * AI › Transcripts — owner only, every view audited.
 *
 * Unchanged in substance from the standalone page this replaces; the only
 * real difference is that it is now reachable from the nav. It previously
 * existed as a route with no nav entry at all, linked only from a button
 * buried at the bottom of the stylist page — undiscoverable, which is a
 * strange property for the panel's most consequential screen.
 *
 * The two deliberate frictions stay exactly as they were:
 *
 *  - **Nothing loads on arrival.** No default list of users, no "recent
 *    conversations" feed. You look up one person, then open one conversation.
 *    The aggregate-by-default rule does not stop applying because the role
 *    gate got stricter.
 *  - **The list shows no message text**, because the API returns none. What
 *    was said arrives only when a specific conversation is opened, which the
 *    backend logs as its own action naming that conversation.
 */
export default function TranscriptsPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q.length < 3) return;
    setBusy(true);
    setUser(null); setConversations(null); setTranscript(null);
    try {
      const { data } = await adminApi.get('/users/search', { params: { q } });
      setResults(data.items);
    } catch (err) {
      toast.error(errMsg(err, 'Search failed'));
    } finally {
      setBusy(false);
    }
  };

  const openUser = async (u) => {
    setBusy(true);
    setUser(u); setTranscript(null); setConversations(null);
    try {
      const { data } = await adminApi.get(`/conversations/${encodeURIComponent(u.user_id)}`);
      setConversations(data.items);
    } catch (err) {
      toast.error(errMsg(err, 'Could not list conversations'));
    } finally {
      setBusy(false);
    }
  };

  const openTranscript = async (conversationId) => {
    setBusy(true);
    try {
      const { data } = await adminApi.get(
        `/conversations/${encodeURIComponent(user.user_id)}/${encodeURIComponent(conversationId)}`,
      );
      setTranscript(data);
    } catch (err) {
      toast.error(errMsg(err, 'Could not open transcript'));
    } finally {
      setBusy(false);
    }
  };

  const conversationColumns = [
    {
      key: 'conversation_id',
      label: 'Conversation',
      render: (c) => <span className="font-mono text-[11px] text-ink">{c.conversation_id}</span>,
    },
    { key: 'last_ts', label: 'When', sortable: true, render: (c) => formatDateTime(c.last_ts) },
    {
      key: 'turns',
      label: 'Turns',
      align: 'right',
      sortable: true,
      render: (c) => <>{c.turns} <span className="text-ink-muted">({c.user_turns} theirs)</span></>,
    },
    { key: 'product_count', label: 'Products shown', align: 'right', sortable: true },
    { key: 'persona_versions', label: 'Persona', render: (c) => c.persona_versions.join(', ') || null },
    { key: 'reached_stages', label: 'Reached', render: (c) => c.reached_stages.join(', ') || null },
    {
      key: 'open',
      label: '',
      align: 'right',
      render: (c) => (
        <button
          onClick={() => openTranscript(c.conversation_id)}
          disabled={busy}
          className="rounded-md border border-hairline px-2 py-1 text-[11px] font-medium text-ink hover:bg-surface-page disabled:opacity-40"
        >
          Read transcript
        </button>
      ),
    },
  ];

  const { sort, onSort } = useSort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sortedConversations = useMemo(() => applySort(conversations || [], sort, conversationColumns), [conversations, sort]);

  return (
    <div>
      <PageHeader
        title="Transcripts"
        hint="Verbatim stylist conversations — the most sensitive data in this panel."
      />

      {/* Not dismissable. It stays on screen for as long as this page is open,
          because "every view is logged" is a fact the reader should be looking
          at while deciding whether to open something. */}
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-status-warning/40 bg-status-warning/5 p-4">
        <svg width="16" height="16" viewBox="0 0 16 16" className="mt-0.5 shrink-0 text-status-warning" fill="currentColor" aria-hidden="true">
          <path d="M8 1L15 14H1L8 1z" />
          <path d="M8 6v4M8 11.5v.5" stroke="#fcfcfb" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
        <div className="text-xs text-ink-secondary">
          <p className="font-semibold text-ink">Owner-only, and every view is written to the audit log.</p>
          <p className="mt-1 leading-relaxed">
            Opening a transcript records who you are, whose conversation it was, and which conversation —
            visible to anyone on the Audit tab. Read one because you need to answer a specific question,
            not to look around. The Stylist tab&apos;s aggregate metrics answer almost everything without
            anyone&apos;s words being read.
          </p>
        </div>
      </div>

      <form onSubmit={search} className="mb-6 flex max-w-xl gap-2">
        <input
          placeholder="Find the user by phone or name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 rounded-lg border border-hairline px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
        />
        <button
          type="submit"
          disabled={busy || query.trim().length < 3}
          className="rounded-lg bg-ink px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          Search
        </button>
      </form>

      {results && results.length === 0 && (
        <EmptyState title="No user matched that" />
      )}

      {results && results.length > 0 && !user && (
        <div className="mb-6 max-w-xl divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-surface shadow-sm">
          {results.map((u) => (
            <button
              key={u.user_id}
              onClick={() => openUser(u)}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-surface-page"
            >
              <span className="text-sm text-ink">{u.name || '(no name)'}</span>
              <span className="font-mono text-[11px] text-ink-muted">{u.user_id}</span>
            </button>
          ))}
        </div>
      )}

      {user && (
        <section className="mb-6">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
              {user.name || user.user_id} — conversations
            </h2>
            <div className="flex items-center gap-3">
              <Link
                to={`/user/${encodeURIComponent(user.user_id)}`}
                className="text-xs font-medium text-raspberry-600 hover:text-raspberry-700"
              >
                Their activity →
              </Link>
              <button
                onClick={() => { setUser(null); setConversations(null); setTranscript(null); }}
                className="text-xs text-ink-secondary hover:text-ink"
              >
                Back to search ✕
              </button>
            </div>
          </div>
          <DataTable
            columns={conversationColumns}
            rows={sortedConversations}
            rowKey={(c) => c.conversation_id}
            loading={conversations === null}
            dense
            sort={sort}
            onSort={onSort}
            empty={<EmptyState title="This user has never chatted with MAHI" />}
          />
        </section>
      )}

      {transcript && (
        <section>
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
              Transcript — {transcript.conversation_id}
            </h2>
            <span className="text-[11px] text-ink-muted">
              {transcript.turn_count} turns · persona {transcript.persona_versions.join(', ') || 'unknown'} ·
              this view has been logged
            </span>
          </div>
          {transcript.truncated && (
            <p className="mb-3 rounded-lg border border-status-warning/40 bg-status-warning/5 px-3 py-2 text-xs text-ink-secondary">
              This conversation is longer than {transcript.max_turns} turns. Only the first {transcript.max_turns}{' '}
              are shown — it is cut off, not summarised.
            </p>
          )}
          <div className="max-w-3xl space-y-3">
            {transcript.turns.map((t, i) => (
              <div
                key={`${t.ts}-${i}`}
                className={`rounded-xl border p-3 ${
                  t.role === 'user' ? 'border-hairline bg-surface' : 'border-raspberry-100 bg-raspberry-50/40'
                }`}
              >
                <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-ink-muted">
                  <span className="font-semibold uppercase tracking-wide text-ink-secondary">
                    {t.role === 'user' ? 'Shopper' : 'MAHI'}
                  </span>
                  <span>{formatDateTime(t.ts)}</span>
                  {t.stage && <span>· {t.stage}</span>}
                  {t.persona_version && <span>· {t.persona_version}</span>}
                </div>
                <p className="whitespace-pre-wrap text-sm text-ink">{t.text}</p>
                {t.product_skus.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {t.product_skus.map((sku) => (
                      <Link
                        key={sku}
                        to={`/product/${encodeURIComponent(sku)}`}
                        className="rounded-full bg-surface-page px-2 py-0.5 font-mono text-[11px] text-ink-muted hover:text-ink"
                      >
                        {sku}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
